/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CLASSIFICATION_BADGE_CONFIG, getBadgeLabelByQuoteStatus, getBadgeTypeByQuoteStatus } from 'src/constants/common';
import { getDateWithSuffixFormat, getFormattedDate, getFormattedDateIgnoringTime, getFormattedNumber, getShortName, onError } from 'src/utils/CommonFunctions';

const QuoteDraggableCell = ({ columnName, data }: { columnName: string; data: any }) => {
    const { t } = useTranslation();
    const codeRef = useRef<HTMLHeadingElement>(null);
    const shipperFullAddressRef = useRef<HTMLParagraphElement>(null);
    const consigneeFullAddressRef = useRef<HTMLParagraphElement>(null);
    const handlingUnitTypesRef = useRef<HTMLParagraphElement>(null);

    if (data === null || data === undefined) return <>-</>;

    const shipperShortAddress = useMemo(() => {
        let shortAddress = '';

        if (data?.shipperCity) {
            shortAddress += `${data?.shipperCity}`;
        }

        if (data?.shipperStateCode) {
            if (shortAddress) {
                shortAddress += `, ${data?.shipperStateCode}`;
            } else {
                shortAddress += `${data?.shipperStateCode}`;
            }
        }

        return shortAddress;
    }, [data?.shipperCity, data?.shipperStateCode]);

    const consigneeShortAddress = useMemo(() => {
        let shortAddress = '';

        if (data?.consigneeCity) {
            shortAddress += `${data?.consigneeCity}`;
        }

        if (data?.consigneeStateCode) {
            if (shortAddress) {
                shortAddress += `, ${data?.consigneeStateCode}`;
            } else {
                shortAddress += `${data?.consigneeStateCode}`;
            }
        }

        return shortAddress;
    }, [data?.consigneeCity, data?.consigneeStateCode]);

    const cellContent = () => {
        switch (columnName) {
            case 'Quote':
                return (
                    <TruncateTextCmp refName={codeRef} message={`#${data?.code}`}>
                        <div ref={codeRef} className="text-grayLight900 font-semibold truncate">
                            #{data?.code}
                        </div>
                    </TruncateTextCmp>
                );
            case 'Created By':
                return (
                    <div className="flex items-center gap-x-2 overflow-hidden">
                        <div className="table-profile-wrap flex-none">
                            {data?.image ? (
                                <img className="table-profile-icn-circle-xs flex-none" src={data?.image} alt={data?.firstName} onError={onError} />
                            ) : (
                                <div className="table-profile-user-circle-xs flex-none">{getShortName(`${data?.firstName} ${data?.lastName}`)}</div>
                            )}
                        </div>
                        <span className="truncate">{data?.fullName}</span>
                    </div>
                );
            case 'Status':
                return (
                    <BadgeCmp style="modern" type={getBadgeTypeByQuoteStatus(data?.status)}>
                        {getBadgeLabelByQuoteStatus(data?.status)}
                    </BadgeCmp>
                );

            case 'Service Type': {
                const config = CLASSIFICATION_BADGE_CONFIG[data?.classification];

                return config ? (
                    <BadgeCmp style="modern" type={config.type}>
                        {t(config.label)}
                    </BadgeCmp>
                ) : null;
            }
            case 'Division':
                return <p className="truncate">{data?.divisionName || '-'}</p>;
            case 'Billing Account':
                return <p className="truncate">{data?.billingAccountCurrency ? data.billingAccountCurrency.toUpperCase() : '-'}</p>;
            case 'From':
                return (
                    <TruncateTextCmp refName={shipperFullAddressRef} message={data?.shipperFullAddress} parentClassName="max-w-90" truncateTextParentClassName="w-full">
                        <p className="font-medium truncate" ref={shipperFullAddressRef}>
                            {shipperShortAddress}
                        </p>
                    </TruncateTextCmp>
                );
            case 'To':
                return (
                    <TruncateTextCmp refName={consigneeFullAddressRef} message={data?.consigneeFullAddress} parentClassName="max-w-90" truncateTextParentClassName="w-full">
                        <p className="font-medium truncate" ref={consigneeFullAddressRef}>
                            {consigneeShortAddress}
                        </p>
                    </TruncateTextCmp>
                );
            case 'Last Modify':
                return (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: data?.updatedAt ? getDateWithSuffixFormat(getFormattedDate(data?.updatedAt, `MMM Do, hh:mm A`, true, true)) : '-',
                        }}
                    />
                );
            case 'Creation Date':
                return (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: data?.createdAt ? getDateWithSuffixFormat(getFormattedDate(data?.createdAt, `MMM Do, hh:mm A`, true, true)) : '-',
                        }}
                    />
                );
            case 'Pickup Date':
                return (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: data?.pickupDate ? getDateWithSuffixFormat(getFormattedDateIgnoringTime(data?.pickupDate, 'ddd, MMM Do, YYYY', true, true, true)) : '-',
                        }}
                    />
                );
            case 'Load Details':
                return (
                    <>
                        {data?.classification === 'ltl' && data?.handlingUnitTypes ? (
                            <TruncateTextCmp refName={handlingUnitTypesRef} message={data?.handlingUnitTypes}>
                                <p className="truncate max-w-[110px]" ref={handlingUnitTypesRef}>
                                    {data?.handlingUnitTypes}
                                </p>
                            </TruncateTextCmp>
                        ) : (
                            <p className="block_ellipsis max-w-[110px]">{data?.primaryEquipmentTypeFullName}</p>
                        )}
                    </>
                );
            case 'Total Weight':
                return <>{data?.totalWeight ? <span>{`${getFormattedNumber(data?.totalWeight, false)} ${data?.weightMeasurement ? data?.weightMeasurement?.toLowerCase() : 'lbs'}`}</span> : <>-</>}</>;

            case 'Quote Type': {
                // Mirrors QuoteCustomerRow.tsx's stopsCount cell — > 2 means at least one genuine
                // customer-visible middle stop (see getQuotes.controller.ts's correlated subquery).
                const stopsTotal = Number(data?.stopsCount ?? 0);
                const intermediateStopCount = stopsTotal > 2 ? stopsTotal - 2 : 0;

                return <span>{intermediateStopCount > 0 ? t('MyQuote.Stops_Column_Count', { count: intermediateStopCount }) : t('MyQuote.Stops_Direct_Option')}</span>;
            }
            default:
                return <>-</>;
        }
    };

    return <>{cellContent()}</>;
};

export default QuoteDraggableCell;
